'use client';

import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@/components/ThemeProvider';
import DashboardLayout from '@/components/DashboardLayout';
import DashboardKPIs from './components/DashboardKPIs';
import DashboardCharts from './components/DashboardCharts';
import RecentOrders from './components/RecentOrders';
import QuickActions from './components/QuickActions';
import TeamSnapshot from './components/TeamSnapshot';
import AcademyProgress from './components/AcademyProgress';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface UserProfile {
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

interface NetworkAccount {
  user_number: number;
  username: string | null;
  package_type: string | null;
}

export default function MemberDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [networkAccount, setNetworkAccount] = useState<NetworkAccount | null>(null);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setDataLoading(false);
      return;
    }

    const supabase = createClient();

    const fetchUserData = async () => {
      try {
        const [profileRes, naRes] = await Promise.all([
          supabase
            .from('user_profiles')
            .select('full_name, first_name, last_name, email')
            .eq('id', user.id)
            .single(),
          supabase
            .from('network_accounts')
            .select('user_number, username, package_type')
            .eq('user_id', user.id)
            .single(),
        ]);

        if (profileRes.data) setProfile(profileRes.data);
        if (naRes.data) setNetworkAccount(naRes.data);
      } catch {
        // silently fail — fallback to auth user data
      } finally {
        setDataLoading(false);
      }
    };

    fetchUserData();
  }, [user, authLoading]);

  const displayName = profile?.full_name
    || (profile?.first_name && profile?.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : null)
    || user?.user_metadata?.full_name
    || user?.email?.split('@')[0]
    || 'Member';

  const packageLabel = networkAccount?.package_type || 'Member';

  const storeSlug = networkAccount?.username
    || user?.email?.split('@')[0]
    || '';

  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w: string) => w[0]?.toUpperCase())
    .join('');

  return (
    <ThemeProvider>
      <DashboardLayout variant="member" userDisplayName={displayName} userInitials={initials} userPackage={packageLabel}>
        <div className="p-6 lg:p-8 max-w-screen-2xl mx-auto space-y-6">
          {/* Welcome header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Welcome back 👋</p>
              {dataLoading ? (
                <div className="h-8 w-48 bg-muted animate-pulse rounded-lg" />
              ) : (
                <h1 className="text-2xl font-bold text-foreground">{displayName}</h1>
              )}
              <div className="flex items-center gap-2 mt-1">
                {dataLoading ? (
                  <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                ) : (
                  <>
                    <span className="richesse-badge bg-primary/10 text-primary text-[10px]">{packageLabel}</span>
                    {storeSlug && (
                      <>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">richesse.global/{storeSlug}</span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
              Last updated: just now
            </div>
          </div>

          <DashboardKPIs />
          <QuickActions />
          <DashboardCharts />
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <RecentOrders />
            </div>
            <div className="space-y-6">
              <TeamSnapshot />
              <AcademyProgress />
            </div>
          </div>
        </div>
      </DashboardLayout>
    </ThemeProvider>
  );
}